import React from 'react';
import { Link } from 'react-router';
import type { LscCommissionerDigest } from '../types';
import { lscPath } from '../routing';
import {
  VOD_INVENTORY_NOT_VERIFIED_DISCLAIMER,
  type VodInventorySummary,
} from '../vodTruth';

export type VodSyncActivityBannerProps = {
  leagueId: string;
  digest: LscCommissionerDigest | null;
  digestError: string | null;
  fallbackSummary: VodInventorySummary;
};

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-sm">
      <dt className="text-secondary-500">{label}</dt>
      <dd className="text-secondary-100 font-medium">{value}</dd>
    </div>
  );
}

const VodSyncActivityBanner: React.FC<VodSyncActivityBannerProps> = ({
  leagueId,
  digest,
  digestError,
  fallbackSummary,
}) => {
  const counts = digest?.counts;
  const summary: VodInventorySummary = counts
    ? {
        sync_activity: counts.new_synced,
        unmatched: counts.synced_unmatched,
        pending_review: counts.pending_review,
        needs_identity: counts.needs_identity_attestation,
        ready_to_publish: counts.ready_to_publish,
        recently_published: counts.recently_published,
        unavailable: counts.unavailable_count ?? fallbackSummary.unavailable,
      }
    : fallbackSummary;

  const showBanner = summary.sync_activity > 0 || summary.unmatched > 0;
  if (!showBanner) {
    return null;
  }

  const unmatchedHref = lscPath(`/leagues/${leagueId}/vods?filter=unmatched`);

  return (
    <div
      className="rounded-lg border border-primary-700/40 bg-primary-900/20 px-4 py-4 space-y-3"
      data-testid="vod-sync-activity-banner"
    >
      <div className="space-y-1">
        <p className="text-sm text-secondary-100">
          Sync activity found VOD candidates in the last 7 days. These are Twitch metadata
          candidates, not verified league games.
        </p>
        <p className="text-xs text-secondary-400">{VOD_INVENTORY_NOT_VERIFIED_DISCLAIMER}</p>
      </div>

      {digestError ? (
        <p className="text-xs text-amber-200" data-testid="vod-sync-activity-banner-digest-error">
          Sync summary unavailable. VOD candidates are still available below.
        </p>
      ) : null}

      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Stat label="Sync activity, 7d" value={summary.sync_activity} />
        <Stat label="Unmatched" value={summary.unmatched} />
        <Stat label="Pending review" value={summary.pending_review} />
        <Stat label="Ready to publish" value={summary.ready_to_publish} />
        <Stat label="Recently published" value={summary.recently_published} />
        {counts ? (
          <Stat label="Member Twitch issues" value={counts.member_twitch_issues} />
        ) : null}
      </dl>

      {summary.unmatched > 0 ? (
        <Link
          to={unmatchedHref}
          className="inline-flex text-sm text-primary-300 hover:text-primary-200"
          data-testid="vod-sync-activity-review-unmatched"
        >
          Review unmatched VODs
        </Link>
      ) : null}
    </div>
  );
};

export default VodSyncActivityBanner;
